import { useSnackbar } from '@/features/snackbar';
import useUser from '@/hooks/useUser';
import {
  AppUpdate as AppUpdatePlugin,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';
import { useQuery } from '@tanstack/react-query';

export function AppUpdate() {
  const { data } = useUser();
  const { loggedIn } = data;
  const { addSnackbar } = useSnackbar();
  useQuery({
    queryKey: ['APP_UPDATES'],
    queryFn: async () => {
      const result = await AppUpdatePlugin.getAppUpdateInfo();
      if (
        result.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE
      ) {
        return;
      }

      if (
        typeof result.updatePriority === 'number' &&
        /**
         * Update priority - https://developer.android.com/guide/playcore/in-app-updates/native#update-priority
         *
         * > To determine priority, Google Play uses an integer value between 0 and 5, with 0 being the default and 5 being the highest priority.
         *
         */
        result.updatePriority === 5 &&
        result.immediateUpdateAllowed
      ) {
        await AppUpdatePlugin.performImmediateUpdate();
        return;
      }

      if (result.flexibleUpdateAllowed) {
        await AppUpdatePlugin.addListener(
          'onFlexibleUpdateStateChange',
          async (state) => {
            if (
              state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED
            ) {
              await AppUpdatePlugin.completeFlexibleUpdate();
            }
          }
        );
        await AppUpdatePlugin.startFlexibleUpdate();
      } else {
        addSnackbar({
          id: 'app-update',
          message: `A new version (${result.availableVersionName}) is now available.`,
          type: 'warning',
          actionLabel: 'Update',
          async actionCallback() {
            await AppUpdatePlugin.openAppStore();
          },
          secondaryActionLabel: 'Dismiss',
        });
      }
    },
    enabled: loggedIn,
    retry: false,
    staleTime: 60000 * 60,
  });
  return null;
}
